import {Card, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {AccountInvoice, useAccountInvoicesQuery} from 'api/generated';
import {AccountInvoiceStatusTag} from 'components/AccountInvoiceStatusTag';
import {TableCard} from 'components/TableCard';
import {
  formatDateRange,
  formatIBAN,
  formatIntAmount,
  formatInvoicePaymentMethod
} from 'lib/formaters';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import styled from 'styled-components';
import {SectionProps} from '../types';
import {InvoiceActions} from './InvoiceActions';

const Amount = styled.span`
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
`;

export const BillingHistory: React.FC<SectionProps> = ({account, cardProps}) => {
  const [from, setFrom] = useState(0);
  const [size, setSize] = useState(10);
  const {accountId} = useParams<{accountId: string}>();
  const {data, loading, previousData} = useAccountInvoicesQuery({
    variables: {
      from,
      size,
      filter: {accountId: {match: accountId}}
    }
  });

  const payload = data ?? previousData;
  const invoices = payload?.accountInvoices.items || [];
  const total = payload?.accountInvoices.total || 0;

  if (!account) return <Card {...cardProps} title="Billing history" />;

  const columns: Array<ColumnProps<AccountInvoice>> = [
    {
      title: 'Number',
      dataIndex: 'number',
      className: 'minimized-cell',
      align: 'left',
      render(number: string) {
        return number || 'N/A';
      }
    },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      className: 'minimized-cell',
      align: 'right',
      render(amount: number, invoice) {
        return <Amount>{formatIntAmount(amount, invoice.currency)}</Amount>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render(_, invoice) {
        return <AccountInvoiceStatusTag invoice={invoice} />;
      }
    },
    {
      title: 'Period',
      dataIndex: 'period',
      render: ({from, to}) => formatDateRange(from, to, 'LL')
    },
    {
      title: 'Payment method',
      dataIndex: 'paymentMethod',
      render: (_, invoice) => formatInvoicePaymentMethod(invoice.paymentMethod)
    },
    {
      title: 'IBAN',
      dataIndex: 'iban',
      align: 'left',
      render: (_, invoice) => formatIBAN(invoice.business?.iban || account.iban, true)
    },
    {
      key: 'actions',
      align: 'right',
      render: (_, invoice) => <InvoiceActions invoice={invoice} account={account} />
    }
  ];

  return (
    <>
      <TableCard {...cardProps} title="Billing history">
        <Table
          rowKey="id"
          dataSource={invoices}
          columns={columns as any}
          loading={loading}
          pagination={{
            total,
            pageSize: size,
            hideOnSinglePage: true,
            current: from / size + 1,
            onChange: (page, size = 0) => {
              setSize(size);
              setFrom((page - 1) * size);
            }
          }}
        />
      </TableCard>
    </>
  );
};
